import React, { useState, useRef } from 'react';
import { Camera, X, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ProfilePictureUpload = ({ currentImageUrl, onUploadSuccess, onUploadError }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const { apiCall, refreshUser } = useAuth();

  // Validate file before upload
  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Please select a valid image file (JPEG, PNG, or WebP)');
    }

    if (file.size > maxSize) {
      throw new Error('File size must be less than 5MB');
    }

    return true;
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    try {
      setUploading(true);
      
      // Validate file
      validateFile(file);

      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Upload to backend (bypass apiCall for file upload to avoid JSON content-type)
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/upload-profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('videbits-token')}`,
          // Don't set Content-Type - let the browser set it with boundary for multipart
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh user data to get updated profile picture
        await refreshUser();
        onUploadSuccess && onUploadSuccess(data.profile_picture_url);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

    } catch (error) {
      console.error('Upload error:', error);
      onUploadError && onUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle file input change
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />

      {/* Upload Area */}
      <div
        className={`relative cursor-pointer transition-all duration-200 ${
          dragActive ? 'scale-105' : 'scale-100'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={triggerFileInput}
      >
        {/* Current Image or Placeholder */}
        <div 
          className={`w-32 h-32 rounded-full flex items-center justify-center border-4 transition-all duration-200 ${
            dragActive 
              ? 'border-videbits-400 border-dashed bg-videbits-50' 
              : uploading 
                ? 'border-gray-300 bg-gray-100'
                : 'border-gray-200 hover:border-videbits-300 hover:shadow-lg'
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center space-y-2">
              <Loader className="w-8 h-8 text-videbits-500 animate-spin" />
              <span className="text-xs text-videbits-600 font-medium">Uploading...</span>
            </div>
          ) : currentImageUrl ? (
            <img
              src={currentImageUrl.startsWith('http') 
                ? currentImageUrl 
                : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${currentImageUrl}`
              }
              alt="Profile"
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <Camera className="w-8 h-8 text-gray-400" />
              <span className="text-xs text-gray-500 text-center">
                {dragActive ? 'Drop image here' : 'Click to upload'}
              </span>
            </div>
          )}
        </div>

      </div>

      {/* Upload Instructions */}
      <div className="text-center">
        <p className="text-sm text-gray-600">
          Drag & drop or click to upload
        </p>
        <p className="text-xs text-gray-500 mt-1">
          JPEG, PNG or WebP • Max 5MB • 200x200px recommended
        </p>
      </div>

      {/* Delete Option */}
      {currentImageUrl && !uploading && (
        <button
          onClick={async (e) => {
            e.stopPropagation();
            try {
              setUploading(true);
              const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/profile-picture`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('videbits-token')}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                // Refresh user data to clear profile picture
                await refreshUser();
                onUploadSuccess && onUploadSuccess(null);
              } else {
                const errorData = await response.json();
                onUploadError && onUploadError(errorData.detail || 'Delete failed');
              }
            } catch (error) {
              onUploadError && onUploadError(error.message);
            } finally {
              setUploading(false);
            }
          }}
          className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-700 transition-colors"
        >
          <X className="w-4 h-4" />
          <span>Remove photo</span>
        </button>
      )}
    </div>
  );
};

export default ProfilePictureUpload;